import { useRef, useState } from "react";
import { adminApi } from "../api.js";
import styles from "../admin.module.css";

interface RowResult {
  index: number;
  ok: boolean;
  errors: string[];
}

interface ImportResponse {
  dryRun: boolean;
  totalRows: number;
  validCount: number;
  invalidCount: number;
  results: RowResult[];
}

export function ImportExport() {
  const [format, setFormat] = useState<"json" | "csv">("json");
  const [data, setData] = useState("");
  const [dryRunResult, setDryRunResult] = useState<ImportResponse | null>(null);
  const [committed, setCommitted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleDryRun() {
    setCommitted(false);
    const result = await adminApi.post<ImportResponse>("/api/admin/events/import", {
      format,
      data,
      dryRun: true,
    });
    setDryRunResult(result);
  }

  async function handleCommit() {
    await adminApi.post<ImportResponse>("/api/admin/events/import", { format, data, dryRun: false });
    setCommitted(true);
    setDryRunResult(null);
  }

  async function handleExport() {
    const snapshot = await adminApi.get("/api/admin/export");
    const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hunch-export-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleFullImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    if (
      !window.confirm(
        "Gesamtimport überschreibt ALLE Karten, Kategorien, Seiten und Spiel-Konfiguration. Fortfahren?",
      )
    ) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    const parsed = JSON.parse(text);
    await adminApi.post("/api/admin/import-full", parsed);
    window.alert("Gesamtimport abgeschlossen.");
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  return (
    <div>
      <h1 className={styles.pageTitle}>Import/Export</h1>

      <div className={styles.card}>
        <h2>Karten-Import (Trockenlauf)</h2>
        <div className={styles.formRow}>
          <label htmlFor="imp-format">Format</label>
          <select
            id="imp-format"
            className={styles.select}
            value={format}
            onChange={(e) => setFormat(e.target.value as "json" | "csv")}
          >
            <option value="json">JSON</option>
            <option value="csv">CSV</option>
          </select>
        </div>
        <div className={styles.formRow}>
          <label htmlFor="imp-data">Daten</label>
          <textarea
            id="imp-data"
            className={`${styles.textarea} ${styles.markdownEditor}`}
            value={data}
            onChange={(e) => setData(e.target.value)}
            placeholder={
              format === "json"
                ? '[{"year": 1969, "fame": 1, "textDe": "...", "textEn": "...", "sourceLabel": "...", "sourceUrl": "...", "status": "draft", "categorySlugs": ["sports"]}]'
                : "year,fame,textDe,textEn,sourceLabel,sourceUrl,status,categorySlugs"
            }
          />
        </div>
        <div className={styles.actions}>
          <button type="button" className={styles.button} onClick={handleDryRun}>
            Trockenlauf
          </button>
          {dryRunResult && dryRunResult.validCount > 0 && (
            <button type="button" className={styles.button} onClick={handleCommit}>
              {dryRunResult.validCount} Karten importieren
            </button>
          )}
        </div>

        {committed && <p>Import abgeschlossen.</p>}

        {dryRunResult && (
          <div>
            <p>
              {dryRunResult.validCount} gültig, {dryRunResult.invalidCount} fehlerhaft von{" "}
              {dryRunResult.totalRows} Zeilen.
            </p>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Zeile</th>
                  <th>Status</th>
                  <th>Fehler</th>
                </tr>
              </thead>
              <tbody>
                {dryRunResult.results.map((r) => (
                  <tr key={r.index}>
                    <td>{r.index + 1}</td>
                    <td>{r.ok ? "OK" : "Fehler"}</td>
                    <td>{r.errors.join("; ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className={styles.card}>
        <h2>Gesamtexport / Gesamtimport</h2>
        <p>
          Exportiert oder ersetzt Kategorien, Karten, Seiten und Spiel-Konfiguration vollständig als
          JSON — z. B. für Backups.
        </p>
        <div className={styles.actions}>
          <button type="button" className={styles.buttonSecondary} onClick={handleExport}>
            Gesamtexport herunterladen
          </button>
          <label className={styles.buttonSecondary}>
            Gesamtimport (Datei wählen)
            <input ref={fileInputRef} type="file" accept="application/json" hidden onChange={handleFullImport} />
          </label>
        </div>
      </div>
    </div>
  );
}
